import React, { FC, useCallback, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { Grid, Typography } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { useDispatch } from 'react-redux'
import { MemoTwoFactorAuthenticationModal } from './TwoFactorAuthenticationModal'
import { InputFieldPassword } from '../../../../components/Dashboard/InputFieldPassword'
import { GreenButton } from '../../../../components/Dashboard/Buttons/GreenButton'
import { InputSwitch } from '../../../../components/Dashboard/InputSwitch'
import { resolver } from './resolver'
import { useRest, useUserIdSf } from '../../../../components/Context'
import { GridRow } from '../../../../components/GridRow'
import { logoutRequest } from '../../../../store/actions'

const useStyles = makeStyles((theme: Theme) => ({
  formWrap: {
    width: '100%',
    paddingTop: theme.spacing(4),
    display: 'flex',
    height: '100%',
    flexDirection: 'column',
  },
  container: {
    paddingBottom: theme.spacing(2),
    minHeight: 40,
    maxHeight: 40,
  },
  sectionWrap: {
    width: '100%',
    height: '100%',
    padding: theme.spacing(4),
    // background: 'rgb(49,52,58)',
    background: theme.palette.background.paper,
  },
  subtitle: {
    color: theme.palette.primary.light,
  },
  form: {
    width: '100%',
  },
  rowWrap: {
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    alignItems: 'center',
    '&>div:nth-child(n)': {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(0),
    },
    '& .MuiFormHelperText-root.Mui-error': {
      margin: 0,
    },
  },
  switchFlex: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}))

type Inputs = {
  currentPassword: string
  newPassword: string
  twoFaEnabled: boolean
}

interface SecuritySectionProps {
  title?: string
}

export const Security: FC<SecuritySectionProps> = ({ title }) => {
  const formMethods = useForm<Inputs>({
    mode: 'onBlur',
    resolver,
  })
  const classes = useStyles()
  const dispatch = useDispatch()
  const { rxiosAuth } = useRest()
  const { retrieveUserIdSf } = useUserIdSf()
  const [twoFactorAuth, setTwoFactorAuth] = useState<boolean>()
  const [isOpenModal, setOpenModal] = useState<boolean>(false)
  const [isTwoFa, setTwoFa] = useState<boolean>(false)

  const { handleSubmit } = formMethods

  const toggleModal = useCallback(() => {
    setOpenModal((prevState) => {
      setTwoFactorAuth(!prevState)
      return !prevState
    })
  }, [setOpenModal])

  const turnOfTwoFactorAuth = useCallback(() => {
    rxiosAuth.post('2fa/turn-of', {}).subscribe(
      // eslint-disable-next-line
      (next: any) => {
        toast.success(next.message)
      },
      (error) => {
        toast.error(error.response.data.message)
      },
    )
  }, [rxiosAuth])

  const getTwoFactorAuth = useCallback(
    (checked: boolean) => {
      setTwoFactorAuth(checked)
      if (checked && !isTwoFa) {
        setOpenModal(checked)
      } else {
        setTwoFa(false)
        turnOfTwoFactorAuth()
      }
    },
    [isTwoFa, turnOfTwoFactorAuth],
  )

  const modalTwoFa = useCallback((checked: boolean) => {
    setTwoFa(checked)
  }, [])

  const getAsyncTwoFactorAuth = useCallback(() => {
    rxiosAuth.get(`2fa/${retrieveUserIdSf()}`).subscribe(
      // eslint-disable-next-line
      (next: any) => {
        const result = { ...next }
        const { isTwoFaEnabled } = result
        setTwoFactorAuth(isTwoFaEnabled)
      },
      (error) => {
        toast.error(error.response.data.message)
      },
    )
  }, [rxiosAuth, retrieveUserIdSf])

  useEffect(() => {
    if (isTwoFa) {
      setOpenModal(false)
    }
  }, [isTwoFa])

  useEffect(() => {
    getAsyncTwoFactorAuth()
    // eslint-disable-next-line
  }, [])

  const logOutHandler = useCallback(() => dispatch(logoutRequest()), [dispatch])

  const onSubmit = useCallback(
    async (data: Inputs) => {
      rxiosAuth.post('user/change-password', data).subscribe(
        // eslint-disable-next-line
        (next: any) => {
          formMethods.reset()
        },
        (error) => {
          toast.error(error.response.data.message)
        },
        () => {
          toast.success('Password was updated successfully')
          logOutHandler()
        },
      )
    },
    // eslint-disable-next-line
    [twoFactorAuth, rxiosAuth],
  )

  return (
    <>
      <Grid item xs={12} className={classes.formWrap}>
        <Grid item xs={12} className={classes.container}>
          <Typography variant={'h6'}>{title}</Typography>
        </Grid>

        <Grid item xs={12} className={classes.sectionWrap}>
          <Grid item xs={12} className={classes.subtitle}>
            <Typography>Change Password</Typography>
          </Grid>

          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
              <GridRow spacing={2} justifyContent="center" alignItems="center" className={classes.rowWrap}>
                <Grid item xs={6}>
                  <InputFieldPassword name={'currentPassword'} placeholder={'Current Password'} />
                </Grid>
                <Grid item xs={6}>
                  <InputFieldPassword name={'newPassword'} placeholder={'New Password'} />
                </Grid>
              </GridRow>

              <GridRow spacing={2} justifyContent="center" alignItems="center" className={classes.rowWrap}>
                <Grid item xs={6}>
                  <GreenButton text="Submit" width={'100%'} />
                </Grid>
                <Grid item xs={6} className={classes.switchFlex}>
                  <InputSwitch
                    label={'Two-Factor Authentication:'}
                    getTwoFactorAuth={getTwoFactorAuth}
                    isChecked={twoFactorAuth}
                  />
                </Grid>
              </GridRow>
            </form>
          </FormProvider>
        </Grid>
      </Grid>
      <MemoTwoFactorAuthenticationModal isOpen={isOpenModal} modalClose={toggleModal} modalTwoFa={modalTwoFa} />
    </>
  )
}
